import { WSButton } from "@wingspanhq/fe-component-library";
import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryInternalAccountBalance } from "../../../../query/banking/queries/useQueryInternalAccountBalance";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { usePayoutSettings } from "../../../../query/payments/queries";
import { useAccounts, useClientQuery } from "../../../../query/users/queries";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorDefaultDestination } from "../../../../shared/selectors/selectorDefaultDestination";
import {
  SelectMethod,
  SelectMethodProps
} from "../../../Settings/components/SelectMethod";
import { PATH_TRANSFER_ADD_BANK_ACCOUNT } from "../../paths";
import { TransferBalance, TransferType } from "../../types";
import { selectorAccountDepositReady } from "./selectorAccountDepositReady";
import { selectorAccountWithdrawalReady } from "./selectorAccountWithdrawalReady";

type DropdownProps = {
  balance: TransferBalance;
  type: TransferType;
} & Omit<SelectMethodProps, "data">;

export const DropdownAccount: React.FC<DropdownProps> = props => {
  const history = useHistory();
  const location = useLocation();

  const userId = useUserId();
  const queryAccounts = useAccounts();
  const queryInternalAccounts = useQueryInternalAccounts();
  const queryClient = useClientQuery(userId);
  const queryPayoutSettings = usePayoutSettings(userId);

  const activeBankingAccount = useMemo(() => {
    return queryInternalAccounts.data?.find(selectorActiveBankingAccount);
  }, [queryInternalAccounts.data]);

  const activeTaxWithholdingAccount = useMemo(() => {
    return queryInternalAccounts.data?.find(
      selectorActiveTaxWithholdingAccount
    );
  }, [queryInternalAccounts.data]);

  const queryBankingBalance = useQueryInternalAccountBalance(
    activeBankingAccount?.internalAccountId!,
    { enabled: !!activeBankingAccount }
  );

  const queryTaxWithholdingBalance = useQueryInternalAccountBalance(
    activeTaxWithholdingAccount?.internalAccountId!,
    { enabled: !!activeTaxWithholdingAccount }
  );

  const isLoading =
    queryAccounts.isInitialLoading ||
    queryInternalAccounts.isInitialLoading ||
    queryClient.isInitialLoading ||
    queryPayoutSettings.isInitialLoading ||
    queryBankingBalance.isInitialLoading ||
    queryTaxWithholdingBalance.isInitialLoading;

  const accounts = useMemo(() => {
    const filterFn =
      props.type === "deposit"
        ? selectorAccountDepositReady
        : selectorAccountWithdrawalReady;
    return queryAccounts.data?.filter(filterFn);
  }, [props.type, queryAccounts.data]);

  const internalAccounts = useMemo(() => {
    if (
      props.balance === "wallet" &&
      activeTaxWithholdingAccount &&
      !!queryTaxWithholdingBalance.data &&
      queryTaxWithholdingBalance.data.balance > 0
    ) {
      return [activeTaxWithholdingAccount];
    }

    if (
      props.balance === "taxWithholdings" &&
      activeBankingAccount &&
      !!queryBankingBalance.data &&
      queryBankingBalance.data.balance > 0
    ) {
      return [activeBankingAccount];
    }
  }, [
    activeBankingAccount,
    activeTaxWithholdingAccount,
    props.balance,
    queryBankingBalance.data,
    queryTaxWithholdingBalance.data
  ]);

  const refIsDefaultSet = useRef(false);

  useEffect(() => {
    if (refIsDefaultSet.current) return;

    if (props.type === "deposit") {
      const defaultPaymentMethodId =
        queryClient.data?.profile.defaultPaymentMethod?.accountId;

      if (defaultPaymentMethodId) {
        const matchingAccount = accounts?.find(
          account => account.accountId === defaultPaymentMethodId
        );

        if (matchingAccount) {
          props.onChange({
            id: defaultPaymentMethodId,
            type: "Account"
          });
          refIsDefaultSet.current = true;
        }
      }
    } else {
      if (!queryPayoutSettings.data) return;

      const defaultPayoutMethodId = selectorDefaultDestination(
        queryPayoutSettings.data
      )?.destinationId;

      if (defaultPayoutMethodId) {
        const matchingAccount = accounts?.find(
          account => account.accountId === defaultPayoutMethodId
        );

        if (matchingAccount) {
          props.onChange({
            id: defaultPayoutMethodId,
            type: "Account"
          });
          refIsDefaultSet.current = true;
        }
      }
    }
  }, [
    accounts,
    props,
    queryClient.data?.profile.defaultPaymentMethod?.accountId,
    queryPayoutSettings.data
  ]);

  return isLoading || accounts?.length || internalAccounts?.length ? (
    <SelectMethod
      status={isLoading ? "loading" : undefined}
      data={{
        accounts,
        internalAccounts
      }}
      value={props.value}
      onChange={props.onChange}
      menuFooterText="Add a new bank account"
      menuFooterAction={{
        label: "Add account",
        onClick: () => {
          history.push(PATH_TRANSFER_ADD_BANK_ACCOUNT + location.search);
        }
      }}
    />
  ) : (
    <WSButton.Tertiary
      fullWidth
      onClick={() =>
        history.push(PATH_TRANSFER_ADD_BANK_ACCOUNT + location.search)
      }
    >
      Add bank account
    </WSButton.Tertiary>
  );
};
